import { useEffect, useState } from 'react';
import { authApi } from '@/services/AuthApi';
import {
  pendingDeploymentInvitation,
  pendingInvitation,
} from '@/features/access/pendingInvitation';

/**
 * How the fixed address reads (I-2181, I-2229): no field's fill and no field's line, but a dashed
 * outline and muted text, so it does not look like something to type into — on the white card and on
 * the dark one, where the card's field rule would draw it as a field (`index.css`, FE-1). Gray-500
 * text keeps 4.8:1 on white. `FixedAddressField` adds the lock.
 */
export const FIXED_ADDRESS_FIELD_CLASSES =
  'cursor-default border border-dashed !border-gray-300 !bg-transparent !shadow-none hover:!bg-transparent focus:!bg-transparent text-gray-500 dark:!border-white/20 dark:text-gray-400';

export interface IInvitedAddress {
  /** The link remembered on this device, when the page was reached from it (`?invited=1`). */
  invitation: string | null;
  /** The address it was sent to, masked (`b•••@evoye.com`); null for a link that names none. */
  masked: string | null;
  /** True until the server has said which address, if any, the link is for. */
  checking: boolean;
}

/**
 * I-2181, RL-5d: a sign-in or sign-up reached from an invitation link (`?invited=1`, set by the invite
 * page). A link sent to one address is only ever redeemed as that address, so the form is fixed to it:
 * the field shows it masked and cannot be changed, and the server reads the address from the link.
 * ⛔ The token is never read from the URL; it is the one the invite page kept on this device.
 */
export function useInvitedAddress(invited: boolean): IInvitedAddress {
  const [invitation] = useState(() => (invited ? pendingInvitation() : null));
  const [masked, setMasked] = useState<string | null>(null);
  const [checking, setChecking] = useState(invitation !== null);

  useEffect(() => {
    if (!invitation) return;
    let cancelled = false;
    // A link the server cannot read leaves the form as it always was; the server still decides.
    void Promise.resolve()
      .then(() => authApi.invitedAddress(invitation))
      .catch(() => null)
      .then((address) => {
        if (cancelled) return;
        setMasked(address);
        setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invitation]);

  return { invitation, masked, checking };
}

/**
 * F-0809 (EAI-1, EAI-6): a sign-up reached from `/-/join` (`?invited=deployment`). A deployment
 * invitation is for one address, so the form is fixed to it, masked, as for a place invitation; the
 * token is the one `/-/join` kept on this device, never read from the URL.
 */
export function useDeploymentInvitedAddress(invited: boolean): IInvitedAddress {
  const [invitation] = useState(() => (invited ? pendingDeploymentInvitation() : null));
  const [masked, setMasked] = useState<string | null>(null);
  const [checking, setChecking] = useState(invitation !== null);

  useEffect(() => {
    if (!invitation) return;
    let cancelled = false;
    void Promise.resolve()
      .then(() => authApi.deploymentInvitationPreview(invitation))
      .then((preview) => preview.maskedEmail)
      .catch(() => null)
      .then((address) => {
        if (cancelled) return;
        setMasked(address);
        setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invitation]);

  return { invitation, masked, checking };
}
